import type { RadioButtonGroupProps } from '@hover/blueprint';
import { RadioButton, RadioButtonGroup } from '@hover/blueprint';
import type { Icon, IconName } from '@hover/icons';
import {
  iconBath,
  iconBed,
  iconFurniture,
  iconOffice,
  iconResidential,
  iconSink,
  iconTable,
} from '@hover/icons';
import type { SVGProps } from 'react';
import React, { useState } from 'react';

import { buttonPressed } from 'src/shared/telemetry';

import { useHomeownerAppContext } from '../../hooks';
import { formatSpaceType } from '../Utils';

const ICONS: {
  [key: string]: Icon<IconName, SVGProps<SVGSVGElement>>;
} = {
  exterior: iconResidential,
  bedroom: iconBed,
  bathroom: iconBath,
  dining_room: iconTable,
  home_office: iconOffice,
  kitchen: iconSink,
  living_room: iconFurniture,
};

export interface SpaceTypeSelectorProps
  extends Omit<RadioButtonGroupProps, 'children'> {
  spaceType: string;
  spaceTypes?: string[];
  setSpaceType: (type: string) => void;
}

const SpaceTypeSelector = ({
  spaceType,
  spaceTypes = [],
  setSpaceType,
  onChange,
  ...props
}: SpaceTypeSelectorProps) => {
  const [focusedType, setFocusedType] = useState<string>();
  const { trackingData } = useHomeownerAppContext();
  const displaySpaceTypes = spaceTypes;

  return (
    <RadioButtonGroup
      data-testid="space-type-selector"
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      gap={{ base: 200, tablet: 400, desktop: 200 }}
      justifyContent={{
        base: 'flex-start',
        tablet: 'center',
        desktop: 'flex-start',
      }}
      onChange={type => {
        buttonPressed({
          ...trackingData,
          track_id: 'upload-image-space-type-selection',
          button_location: 'middle-main-page',
          button_text: type,
        });

        setSpaceType(type);
        onChange?.(type);
      }}
      value={spaceType}
      {...props}
    >
      {displaySpaceTypes.map(type => (
        <RadioButton
          gap={200}
          iconBefore={ICONS[type]}
          key={type}
          mx={{ base: focusedType === type ? 100 : 0, tablet: 'unset' }}
          onBlur={() => setFocusedType(undefined)}
          onFocus={() => setFocusedType(type)}
          size={{ base: 'medium', tablet: 'large' }}
          transition="margin 0.3s"
          value={type}
        >
          {formatSpaceType(type)}
        </RadioButton>
      ))}
      {displaySpaceTypes.length === 0 && (
        <RadioButton isDisabled isLoading width={600} />
      )}
    </RadioButtonGroup>
  );
};

export { SpaceTypeSelector };
